import EngineCleanupBase from '../../services/engines/EngineCleanupBase';
import responseHelper from '../../services/ResponseHelper';
import { subscribe } from '../../services/utils/pubsub';
import windowManagerService from '../../services/WindowManagerService';
import ComponentNotifier from '../../services/wss/ComponentNotifier';
import jxWindowCache from './JxWindowCache';
import { JxWindowEvents } from './JxWindowEvents';
import windowPositionPublisher from './windowPositionPublisher';

const _name = 'JxWindowEngine';

/**
 * Orchestrates window position/state for all JxForm windows.
 * Cache keyed on windowId (this.getId()). Singleton — shared across the app.
 *
 * Bootup: TODO once a BE endpoint exists — load all saved window positions into cache.
 * Until then, getPos() returns null for every window and JxFormDraggableBase falls back
 * to getInitialPos().
 */
class JxWindowEngine extends EngineCleanupBase {
    _onWindowEvent = null;
    _topZIndex = 1500;

    bootup = () => {
        try {
            this.initialize(_name);
            this._onWindowEvent = subscribe(JxWindowEvents.channel, this._handleWindowEvent);
        } catch (error) {
            this.logError('bootup', error.message);
        }
    };

    fetchGeo = async () => {
        try {
            // window geo's for the current browser tab + active workspace
            const tabId = this.getGeoScopeId();
            const resp = await windowManagerService.getWindowPositions(tabId);
            if (responseHelper.check(resp, '', `${this._name}.fetchGeo`) < 0) {
                //  this.setState({ items: [] });
                this.logError('fetchGeo', `code: ${resp?.status}`);
                return;
            }

            const items = resp.data.items;

            jxWindowCache.hydrate(items);
        } catch (error) {
            this.logError('fetchGeo', error.message);
        }
    };

    /**
     * BE/cache field names (posX/posY) never leave this method — everything above this
     * layer only ever sees top/left/width/height. That translation lives here, once.
     * @param {string} windowId
     * @returns {{top:number,left:number,width:number,height:number}|null} null if never cached
     * — caller falls back to getDefaultGeo()
     */
    getPos = (windowId) => {
        try {
            const cached = jxWindowCache.getByWindowId(windowId);
            if (!cached) return null;

            if (cached.posY < 0) {
                cached.posY = 0;
            }
            if (cached.posX < 0) {
                cached.posX = 0;
            }

            return { top: cached.posY, left: cached.posX, width: cached.width, height: cached.height };
        } catch (error) {
            this.logError('getPos', error.message);
            return null;
        }
    };

    _handleWindowEvent = ({ name, target, data }) => {
        try {
            if (name === JxWindowEvents.onPosChanged) {
                const geo = jxWindowCache.setByWindowId(target, data);

                this.logDebug('_handleWindowEvent', `onPosChanged ${target} - ${JSON.stringify(data)}`);

                //todo send to BE
                windowPositionPublisher.publish(geo);
                return;
            }

            if (name === JxWindowEvents.onSizeChanged) {
                const geo = jxWindowCache.setByWindowId(target, data);

                this.logDebug('_handleWindowEvent', `onSizeChanged ${target} - ${JSON.stringify(data)}`);

                //todo send to BE
                windowPositionPublisher.publish(geo);
                return;
            }

            if (name === JxWindowEvents.onWindowSelected) {
                this._topZIndex += 1;
                // Only the selected window gets a new (top) zIndex. Non-selected windows
                // are NOT reset here — they keep whatever zIndex they already have in their
                // own component state (see JxFormBase._applyZIndex). Broadcasting a shared
                // "zIndexOther" to everyone used to collapse all background windows onto the
                // same zIndex, destroying their relative stacking order.
                ComponentNotifier.notify(_name, 'SelectWindow', { id: target, zIndex: this._topZIndex });
                this.logDebug('_handleWindowEvent', `onWindowSelected ${target} zIndex=${this._topZIndex}`);
                return;
            }
        } catch (error) {
            this.logError('_handleWindowEvent', error.message);
        }
    };

    clear() {
        try {
            super.clear();
            if (this._onWindowEvent) this._onWindowEvent = this._onWindowEvent();
        } catch (error) {
            this.logError('clear', error.message);
        }
    }
}

const jxWindowEngine = new JxWindowEngine();
export default jxWindowEngine;
